import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { useDispatch } from 'react-redux';

import { identifyUser } from '@/helpers';
import getDeviceId from '@/helpers/getDeviceId';
import { getUserQueryOptions } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { setSessionChecked, setUserData, unsetUserData } from '@/redux/slices/user/actions';
import { UserState } from '@/redux/slices/user/types';
import { buildURL, patch, post } from '@/shared/fetch';
import { getClientId } from '@/shared/ga';
import { trackMP } from '@/shared/mp';
import { clearPostAuthDestination } from '@/shared/postAuthDestination';

export const useLogout = (onLogout?: () => void) => {
  const dispatch = useDispatch();

  const { mutate: logout } = useMutation({
    mutationFn: () => post(buildURL('/users/logout'), { deviceId: getDeviceId() }),
    onSuccess: () => {
      onLogout?.();
      localStorage.removeItem('giftModal');
      clearPostAuthDestination();
      dispatch(unsetUserData());
      route('/login');
    },
  });

  return useCallback(() => logout(), [logout]);
};

const updateUserGAClientIds = async (user: { gaClientIds?: string[]; _id?: string }) => {
  const clientId = await getClientId();
  const userClientIds = user.gaClientIds ?? [];
  if (!userClientIds.includes(clientId) && user?._id) {
    await patch(buildURL(`/users/${user._id}/clientId`), {
      gaClientIds: [...userClientIds, clientId],
    });
  }
};

export const useFetchUserIfNeeded = (onResponse?: (user: UserState | null, error?: Error) => void) => {
  const dispatch = useDispatch();
  const sessionChecked = useSelector((state) => state.user.sessionChecked);
  const { data, isSuccess, isLoading, isError } = useQuery({
    ...getUserQueryOptions(),
    enabled: !sessionChecked,
  });

  const onResponseRef = useRef(onResponse);
  useEffect(() => {
    onResponseRef.current = onResponse;
  }, [onResponse]);

  useEffect(() => {
    if (sessionChecked) {
      return;
    }

    if (isSuccess) {
      dispatch(setSessionChecked());

      if (!data) {
        dispatch(unsetUserData());
      } else {
        dispatch(setUserData(data));
        updateUserGAClientIds(data);
        identifyUser(data as { _id: string; role: string });
        trackMP('login', { method: 'automatic' });
      }
    } else if (isError) {
      // Resolve the session even on failure so the app degrades to guest instead of hanging on
      // AppLoader forever (every guard waits on sessionChecked). 401s already unset the user inside
      // handleResponse; this covers 5xx / network errors.
      dispatch(setSessionChecked());
    }

    if (!isLoading) {
      onResponseRef.current?.(data ?? null, isError ? new Error('Failed to fetch user data') : undefined);
    }
  }, [data, dispatch, isError, isLoading, isSuccess, sessionChecked]);
};
